import { SetStateAction } from 'react';
import { RcFile } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import { useTranslation } from 'react-i18next';

import {
  DatePickerInput,
  Form,
  PhoneNumberInput,
  PLATFORM_THEME_COLORS,
  TextInput,
  Typography,
  UploadInput,
} from '@thinknet-gmbh/platform-common-ui';

import { GeneralFormItemsWrapper, MainWrapper, UploadWrapper } from './general-form-items.styles';
import { APPLICANT_FILE_MAX_SIZE } from './general-form-items.constants';

const GeneralFormItems = ({
  setDocuments,
  docs,
}: {
  setDocuments: SetStateAction<any>;
  docs: any;
}) => {
  const { t } = useTranslation(['common', 'vacancies']);

  const onUpload = (file: RcFile, key: string) => {
    setDocuments((prev: any) => ({
      ...prev,
      [key]: [...prev[key], file],
    }));

    return false;
  };

  const onRemove = (file: UploadFile<any>, key: string) => {
    const docsCopy = [...docs[key]];

    const index = docsCopy.findIndex((item: any) => item.uid === file.uid);

    docsCopy.splice(index, 1);

    setDocuments((prev: any) => ({ ...prev, [key]: docsCopy }));
  };

  return (
    <MainWrapper>
      <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.murky}>
        {t('vacancies:labels.general')}
      </Typography>
      <GeneralFormItemsWrapper>
        <div>
          <Form.Item
            name="firstName"
            label={t('common:labels.firstName')}
            rules={[{ required: true }]}
          >
            <TextInput maxLength={50} placeholder="Ulf" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={t('common:labels.lastName')}
            rules={[{ required: true }]}
          >
            <TextInput maxLength={50} placeholder="Muller" />
          </Form.Item>
          <Form.Item
            name="email"
            label="E-mail"
            rules={[
              { required: true },
              { type: 'email', message: t('common:validation.error.incorrectFormat') },
            ]}
          >
            <TextInput maxLength={50} placeholder="ulfmuller@outlook.com" />
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label={t('common:labels.phoneNumber')}
            rules={[{ required: true }]}
          >
            <PhoneNumberInput placeholder=" +49 30 901820" />
          </Form.Item>
        </div>
        <div>
          <Form.Item
            name="availableFrom"
            label={t('vacancies:labels.availableFrom')}
            rules={[{ required: true }]}
          >
            <DatePickerInput format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="salaryExpectations" label={t('vacancies:labels.salaryExpectations')}>
            <TextInput placeholder={t('vacancies:labels.sum')} maxLength={50} />
          </Form.Item>
        </div>
      </GeneralFormItemsWrapper>
      <UploadWrapper>
        <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.murky}>
          {t('settings:documents.labels.documents')}
        </Typography>
        <div>
          <Form.Item
            name={['documents', 'resume']}
            label={t('vacancies:labels.cv')}
            rules={[
              { required: true },
              {
                validator: (_, value: any) => {
                  if (value && !value.fileList.length) {
                    return Promise.resolve();
                  }
                  if (
                    value &&
                    value.fileList.some((file: any) => file.size > APPLICANT_FILE_MAX_SIZE)
                  ) {
                    return Promise.reject();
                  }

                  return Promise.resolve();
                },

                message: t('vacancies:validation.maxFileSize'),
              },
              {
                validator: (_, value: any) => {
                  if (value && !value.fileList.length) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
                message: t('common:validation.error.fieldRequired'),
              },
            ]}
            valuePropName="file"
          >
            <UploadInput
              variant="button"
              btnText={t('settings:documents.buttons.uploadFile')}
              accept=".doc, .docx, .pdf, .xls, .xlsx"
              showUploadList
              beforeUpload={file => onUpload(file, 'resumeFiles')}
              onRemove={file => onRemove(file, 'resumeFiles')}
            />
          </Form.Item>
          <Form.Item
            name={['documents', 'salutation']}
            label={t('customers:labels.salutation')}
            valuePropName="file"
            rules={[
              {
                validator: (_, value: any) => {
                  if (!value || !value.fileList.length) return Promise.resolve();
                  if (value.fileList.some((file: any) => file.size > APPLICANT_FILE_MAX_SIZE))
                    return Promise.reject();

                  return Promise.resolve();
                },
                message: t('vacancies:validation.maxFileSize'),
              },
            ]}
          >
            <UploadInput
              variant="button"
              btnText={t('settings:documents.buttons.uploadFile')}
              accept=".doc, .docx, .pdf, .xls, .xlsx"
              showUploadList
              beforeUpload={file => onUpload(file, 'salutationFiles')}
              onRemove={file => onRemove(file, 'salutationFiles')}
            />
          </Form.Item>
        </div>
      </UploadWrapper>
    </MainWrapper>
  );
};

export default GeneralFormItems;
